import { normalizeMulti } from "./commercialProjectSchema";
import { listMatrixUserEmailsByRoleName } from "./commercialRoleAccess";

/** Distinct chip colors for construction events (construction-lead calendar view). */
export const CONSTRUCTION_LEAD_CALENDAR_COLORS = [
  "#2563eb",
  "#7c3aed",
  "#059669",
  "#d97706",
  "#dc2626",
  "#0891b2",
  "#be185d",
  "#4f46e5",
  "#0d9488",
  "#ca8a04",
  "#9333ea",
  "#ea580c",
];

export function normalizeConstructionLeadKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

export function formatConstructionLeadLabel(email) {
  const normalized = normalizeConstructionLeadKey(email);
  if (!normalized || normalized === "unknown") return "Unknown construction lead";
  const local = normalized.split("@")[0] || normalized;
  const pretty = local.replace(/[._-]+/g, " ").trim();
  if (!pretty) return normalized;
  return pretty
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function constructionLeadInitials(email) {
  const label = formatConstructionLeadLabel(email);
  if (label === "Unknown construction lead") return "?";
  const parts = label.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return label.slice(0, 2).toUpperCase();
}

/** Resolve the primary construction lead from schedule.construction_foremen (legacy key). */
export function resolveConstructionLeadKey(schedule) {
  const leads = normalizeMulti(schedule?.construction_foremen);
  const first = leads.map((name) => normalizeConstructionLeadKey(name)).find(Boolean);
  return first || "unknown";
}

export function isConstructionOnlyFilter(filters, { requireReportFiltersOff = true } = {}) {
  if (filters?.construction !== true) return false;
  if (filters?.splicing !== false || filters?.networking !== false) return false;
  if (requireReportFiltersOff) {
    if (filters?.inspectorReports !== false) return false;
    if (filters?.crewReports !== false) return false;
  }
  return true;
}

export function listTrackedConstructionLeadEmails(extraEmails = []) {
  const seen = new Set();
  const out = [];
  for (const email of listMatrixUserEmailsByRoleName("Construction Lead")) {
    const key = normalizeConstructionLeadKey(email);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  for (const email of extraEmails) {
    const key = normalizeConstructionLeadKey(email);
    if (!key || key === "unknown" || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

export function buildConstructionLeadColorMap(leadEmails) {
  const map = new Map();
  leadEmails.forEach((email, index) => {
    map.set(
      normalizeConstructionLeadKey(email),
      CONSTRUCTION_LEAD_CALENDAR_COLORS[index % CONSTRUCTION_LEAD_CALENDAR_COLORS.length]
    );
  });
  return map;
}

export function applyConstructionLeadColorsToEvents(events, colorMap) {
  return events.map((event) => {
    if (event.eventType !== "construction") return event;
    const leadKey = normalizeConstructionLeadKey(event.constructionLeadKey);
    const color = colorMap.get(leadKey) || CONSTRUCTION_LEAD_CALENDAR_COLORS[0];
    return {
      ...event,
      color,
      typeShort: constructionLeadInitials(leadKey),
      constructionLeadLabel: formatConstructionLeadLabel(leadKey),
    };
  });
}
